/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, DestroyRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { PathElement } from '@alfresco/js-api';
import { NgForOf, NgIf } from '@angular/common';
import { NODE_CHILDREN_PAGE_SIZE, NodesService } from '../../services/nodes.service';
import { AuthenticationService, ThumbnailService } from '@alfresco/adf-core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlgMaterialModule } from '../../alg-material.module';

export interface TreeNode {
  name: string;
  isFile: boolean;
  nodeId?: string;
  loadNext?: boolean;
  nodeParentId?: string;
  expanded?: boolean;
  children?: TreeNode[];
  skipCount?: number;
  current?: boolean;
  order?: number;

  /** Type MIME du fichier, pour resoudre son icone. Absent sur un dossier. */
  mimeType?: string;

  /**
   * Dossier connu comme vide.
   *
   * `undefined` signifie « pas encore su ». Le modele de noeud d'Alfresco ne porte aucun compteur
   * d'enfants : l'information vient d'une sonde dediee, lancee des qu'un niveau s'affiche.
   */
  empty?: boolean;
}

@Component({
  selector: 'app-tree-nodes',
  templateUrl: './tree-nodes.html',
  styleUrls: ['./tree-nodes.scss'],
  standalone: true,
  imports: [AlgMaterialModule, NgIf, NgForOf],
  encapsulation: ViewEncapsulation.None
})
export class TreeNodesComponent implements OnInit, OnDestroy {
  /** Racine de tous les sites. Sa presence dans le chemin signale une bibliotheque. */
  private static readonly SITES = 'Sites';

  /** Conteneur de documents d'un site : c'est la que demarre l'arborescence d'une bibliotheque. */
  private static readonly DOCUMENT_LIBRARY = 'documentLibrary';

  /** Parent de tous les dossiers personnels. Celui qui le suit est le dossier de l'utilisateur. */
  private static readonly USER_HOMES = 'User Homes';

  /** Libelle de la racine des fichiers personnels, a la place du nom de compte. */
  private static readonly PERSONAL_FILES_LABEL = 'Fichiers Personnels';

  @Input() node: any;

  /** Chaine d'ancetres intacte, fournie par la page. Voir `treeChain()`. */
  @Input() nodePath: PathElement[];

  @Input() treeData: TreeNode[] = [];
  @Input() rootNode: TreeNode[];
  isCtrlPressed = false;

  // Set et non tableau : la recherche etait un balayage lineaire rejoue pour chaque enfant charge.
  nodesAlreadyLoaded = new Set<string>();
  private tempTreeData: TreeNode[] = [];
  private firstIteration = true;
  private nodeSaved: TreeNode;

  /**
   * Un fichier a ete choisi dans l'arborescence.
   *
   * La page l'ouvre dans son volet de previsualisation, exactement comme un clic dans la liste.
   * L'arborescence n'ouvrait auparavant les fichiers que dans un nouvel onglet — le seul chemin qui
   * y menait etant la recherche dans le dossier courant, puisque le gabarit ecartait les fichiers.
   */
  @Output() fileSelected = new EventEmitter<TreeNode>();

  private readonly nodesService = inject(NodesService);
  private readonly authService = inject(AuthenticationService);
  private readonly router = inject(Router);
  private readonly thumbnailService = inject(ThumbnailService);
  private readonly destroyRef = inject(DestroyRef);

  // Lies une seule fois : `.bind()` rend une nouvelle fonction a chaque appel, et
  // `removeEventListener` ne peut retirer que la reference exacte qui a ete enregistree.
  private readonly onKeyDownBound = this.onKeyDown.bind(this);
  private readonly onKeyUpBound = this.onKeyUp.bind(this);
  private readonly onWindowBlurBound = this.onWindowBlur.bind(this);

  // Methode d'écoute sur l'url pour mettre à jour le noeud courant
  nodeUpdated() {
    const currentUrl = this.router.url;
    const segments = currentUrl.split('/');
    const nodeId = segments[segments.length - 1];
    if (nodeId) {
      const foundNode = this.findNodeById(this.treeData, nodeId);

      // Seuls les DOSSIERS sont deselectionnes : un changement d'URL ne doit pas effacer le
      // fichier en cours de previsualisation, qui peut precisement vivre dans un autre dossier.
      this.clearFolderSelection();

      if (foundNode) {
        foundNode.current = true;
        this.treeData.map((node) => {
          if (node.nodeId !== foundNode.nodeId) {
            node.current = false;
          }
        });
        foundNode.expanded = true;
      }
    }
  }

  isExpanded(node: TreeNode): boolean {
    return node.children && node.children.length > 0;
  }

  /** Evite de recreer toute la liste a chaque rafraichissement d'un niveau. */
  trackByNodeId(_index: number, node: TreeNode): string {
    return node.nodeId ?? node.name;
  }

  /**
   * Icone d'un fichier, d'apres son type MIME.
   *
   * `ThumbnailService` rend un chemin vers une ressource SVG livree par ADF (Word, Excel, PDF, image,
   * archive...), et non un nom d'icone Material : le gabarit l'affiche donc dans un `<img>`. C'est
   * la meme resolution que celle qu'emploie la liste de documents, d'ou la coherence des icones
   * entre les deux vues.
   */
  fileIcon(node: TreeNode): string {
    return node.mimeType ? this.thumbnailService.getMimeTypeIcon(node.mimeType) : this.thumbnailService.getDefaultMimeTypeIcon();
  }

  /**
   * Un dossier connu comme vide n'a pas de chevron — et un fichier n'en a jamais.
   *
   * `empty` est renseigne par `probeEmptiness` des qu'un niveau s'affiche. Tant qu'il vaut
   * `undefined`, le chevron reste propose : mieux vaut un chevron de trop qu'un dossier qu'on ne
   * peut plus ouvrir.
   */
  canExpand(node: TreeNode): boolean {
    return !node.isFile && node.empty !== true;
  }

  /**
   * Un noeud d'arborescence, depuis une entree de l'API.
   *
   * `mimeType` sert a resoudre l'icone du fichier. Il vaut `undefined` pour un dossier, ce qui est
   * exactement la distinction dont le gabarit a besoin.
   */
  private toTreeNode(entry: any): TreeNode {
    return {
      name: entry.name,
      isFile: entry.isFile,
      nodeId: entry.id,
      nodeParentId: entry.parentId,
      mimeType: entry.content?.mimeType,
      expanded: false,
      children: []
    };
  }

  /**
   * Determine quels dossiers d'un niveau sont vides, pour que le chevron n'apparaisse que la ou il
   * mene quelque part.
   *
   * Une requete par dossier, minuscule, toutes en parallele. C'est le prix a payer : le modele de
   * noeud d'Alfresco ne porte aucun compteur d'enfants, et la requete a facettes sur `PARENT` — qui
   * aurait rendu tous les comptes d'un niveau en un seul appel — echoue en 500 sur cette version.
   *
   * Seuls les dossiers encore inconnus sont interroges : l'information, une fois acquise, ne
   * s'oublie pas.
   */
  private probeEmptiness(nodes: TreeNode[]): void {
    const unknown = nodes.filter((child) => !child.isFile && !child.loadNext && child.nodeId && child.empty === undefined);

    if (!unknown.length) {
      return;
    }

    forkJoin(unknown.map((child) => this.nodesService.hasChildren(child.nodeId)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (results) => results.forEach((hasChildren, index) => (unknown[index].empty = !hasChildren)),
        error: () => {
          // Sonde en echec : on laisse `empty` indefini, donc le chevron propose. Mieux vaut un
          // chevron de trop qu'un dossier qu'on ne peut plus ouvrir.
        }
      });
  }

  /** Ligne « charger les suivants », qui n'est pas un noeud du depot. */
  private loadNextRow(parentId: string): TreeNode {
    return {
      name: 'Charger les dossiers suivants',
      loadNext: true,
      isFile: false,
      expanded: true,
      nodeParentId: parentId
    };
  }

  toggleNode(node: TreeNode): void {
    if (node.children?.length === 0) {
      this.nodesService.getNodeChildren(node.nodeId).subscribe((children) => {
        children.list.entries.map((x) => {
          if (!this.nodesAlreadyLoaded.has(x.entry.id)) {
            node.children.push(this.toTreeNode(x.entry));
          }
        });
        if (children.list.pagination.hasMoreItems) {
          node.skipCount = children.list.pagination.skipCount + NODE_CHILDREN_PAGE_SIZE;
          node.children.push(this.loadNextRow(node.nodeId));
        }

        // Le dossier est desormais connu comme vide ou non : le gabarit peut cesser de proposer un
        // chevron. C'est le seul moment ou l'information est disponible sans requete dediee — le
        // modele de noeud d'Alfresco ne porte aucun compteur d'enfants.
        node.empty = node.children.length === 0;
        this.probeEmptiness(node.children);
      });
    }
    node.expanded = !node.expanded;
  }

  navigateToFromOutside(nodeId: string, fromSearch: boolean = false): void {
    const node = this.findNodeById(this.treeData, nodeId);
    if (node) {
      this.navigateTo(node);
    } else {
      this.loadAndOpenPathToNode(nodeId, fromSearch);
    }
  }

  loadAndOpenPathToNode(nodeId: string, fromSearch: boolean = false): void {
    if (fromSearch) {
      this.nodesService.getNode(nodeId).subscribe((node) => {
        const nodeDetail: TreeNode = {
          ...this.toTreeNode(node.entry),
          expanded: true,
          skipCount: 0,
          current: this.firstIteration,
          children: this.tempTreeData
        };
        if (this.firstIteration) {
          this.nodeSaved = nodeDetail;
        }
        this.firstIteration = false;
        // Récupérer les enfants du noeud
        this.nodesService.getNodeChildren(nodeId).subscribe((children) => {
          children.list.entries.map((x) => {
            if (!this.nodesAlreadyLoaded.has(x.entry.id)) {
              nodeDetail.children.push(this.toTreeNode(x.entry));
            }
          });
          if (children.list.pagination.hasMoreItems) {
            nodeDetail.skipCount = children.list.pagination.skipCount + NODE_CHILDREN_PAGE_SIZE;
            nodeDetail.children.push(this.loadNextRow(nodeDetail.nodeId));
          }
          nodeDetail.empty = nodeDetail.children.length === 0;
          this.probeEmptiness(nodeDetail.children);
        });
        this.tempTreeData = [nodeDetail];
        if (nodeDetail.nodeParentId) {
          if (nodeDetail.nodeParentId === this.node.id) {
            this.treeData.map((treeNode) => {
              if (treeNode.nodeId === nodeDetail.nodeParentId) {
                treeNode.children.unshift(nodeDetail);
                this.nodesAlreadyLoaded.add(this.nodeSaved.nodeId);
                this.navigateTo(this.nodeSaved);
              }
            });
          } else {
            this.loadAndOpenPathToNode(nodeDetail.nodeParentId, fromSearch);
          }
        }
      });
    } else {
      // récupérer le node et l'ajouter aux children du this.node
      this.nodesService.getNode(nodeId).subscribe((node) => {
        const newNode: TreeNode = {
          ...this.toTreeNode(node.entry),
          expanded: true,
          skipCount: 0,
          current: true,
          children: []
        };
        this.findNodeById(this.treeData, this.node.id).children.push(newNode);

        this.navigateTo(newNode);
      });
    }
  }

  navigateTo(node: TreeNode): void {
    const currentUrl = this.router.url;
    const segments = currentUrl.split('/');

    if (node.isFile) {
      // Ctrl enfonce : nouvel onglet, comme partout ailleurs dans l'application. Sinon la page
      // previsualise le fichier dans son volet, au meme titre qu'un clic dans la liste.
      if (this.isCtrlPressed) {
        segments[segments.length - 1] = node.nodeParentId;
        const newUrl = segments.join('/');
        const start = location.href.includes('localhost') ? '/#' : '/aca/#';
        window.open(start + newUrl + '/(viewer:view/' + node.nodeId + ')', '_blank');
        return;
      }

      // La page se charge du reste : c'est elle qui detient l'instance RACINE de l'arborescence,
      // donc la seule a pouvoir deselectionner un fichier situe dans une autre branche. Une
      // instance imbriquee ne voit que son propre sous-arbre.
      this.fileSelected.emit(node);
      return;
    }

    this.deleteCurrentOfNodes(this.treeData);
    this.deleteCurrentOfNodes(this.rootNode);

    if (node.nodeId !== this.node?.id) {
      if (this.isCtrlPressed) {
        const url = window.location.href.split('/');
        url[url.length - 1] = node.nodeId;
        if (currentUrl.includes('personal-files') && !url.join('/').includes('personal-files')) {
          url.unshift('/personal-files');
        }
        window.open(url.join('/'), '_blank');
      } else {
        segments[segments.length - 1] = node.nodeId;
        let newUrl = segments.join('/');
        if (currentUrl.includes('personal-files') && !newUrl.includes('personal-files')) {
          newUrl = '/personal-files' + newUrl;
        }
        this.router.navigate([newUrl]);
        if (!node.expanded) {
          this.toggleNode(node);
        }
        node.current = true;
      }
    }
  }

  deleteCurrentOfNodes(nodes: TreeNode[]): void {
    nodes?.map((node) => {
      node.current = false;
      if (node.children && node.children.length > 0) {
        this.deleteCurrentOfNodes(node.children);
      }
    });
  }

  /**
   * Deselectionne les fichiers, et eux seuls.
   *
   * Appele par la page a la fermeture du volet de previsualisation : le fichier ne doit plus
   * apparaitre selectionne dans l'arborescence, mais le dossier courant garde sa mise en evidence.
   *
   * L'arborescence est rendue recursivement, mais ses donnees sont un seul arbre porte par
   * l'instance racine : parcourir `treeData` depuis elle suffit a atteindre tous les niveaux.
   */
  clearFileSelection(nodes: TreeNode[] = this.treeData): void {
    nodes?.forEach((node) => {
      if (node.isFile) {
        node.current = false;
      }
      if (node.children?.length) {
        this.clearFileSelection(node.children);
      }
    });
  }

  /** Deselectionne les dossiers, et eux seuls. Pendant de `clearFileSelection`. */
  clearFolderSelection(nodes: TreeNode[] = this.treeData): void {
    nodes?.forEach((node) => {
      if (!node.isFile) {
        node.current = false;
      }
      if (node.children?.length) {
        this.clearFolderSelection(node.children);
      }
    });
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Control') {
      this.isCtrlPressed = true;
    }
  }

  onKeyUp(event: KeyboardEvent) {
    if (event.key === 'Control') {
      this.isCtrlPressed = false;
    }
  }

  onWindowBlur() {
    this.isCtrlPressed = false;
  }

  ngOnInit() {
    window.addEventListener('keydown', this.onKeyDownBound);
    window.addEventListener('keyup', this.onKeyUpBound);
    window.addEventListener('blur', this.onWindowBlurBound);
    this.setupTreeData();

    // Surveille les changements d'URL pour mettre a jour le noeud courant.
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.nodeUpdated();
      }
    });
  }

  /**
   * L'arborescence est reinstanciee a chaque page Fichiers, et elle est recursive : sans ce
   * nettoyage, chaque instance laissait derriere elle trois ecouteurs sur `window` et un abonnement
   * aux evenements du routeur, tous porteurs d'une reference vers le composant.
   */
  ngOnDestroy() {
    window.removeEventListener('keydown', this.onKeyDownBound);
    window.removeEventListener('keyup', this.onKeyUpBound);
    window.removeEventListener('blur', this.onWindowBlurBound);
  }

  setupTreeData(): void {
    if (this.node?.id) {
      this.buildTree();
    }
  }

  /**
   * Ou commence l'arborescence, et faut-il montrer ce premier niveau.
   *
   * Deux regles metier :
   *
   * - **Bibliotheques** : on demarre a `documentLibrary`, etiquete du nom du site. Demarrer plus
   *   haut exposait `dataLists`, `links` et les autres conteneurs du site, qui n'ont rien a faire
   *   dans une arborescence de documents.
   * - **Fichiers personnels** : on demarre au dossier de l'utilisateur, mais sous le libelle
   *   « Fichiers Personnels » plutot que sous son nom de compte. C'est la ligne qui permet de
   *   revenir a la racine.
   *
   * La chaine vient de `nodePath`, la copie INTACTE de `node.path.elements` prise par la page avant
   * qu'elle ne la charcute pour le fil d'Ariane. Elle contient tous les ancetres, donc aucun appel
   * n'est necessaire pour remonter la hierarchie — la version precedente en faisait un par niveau.
   */
  private treeChain(): { id: string; name: string }[] {
    const elements = (this.nodePath ?? []).map((element) => ({ id: element.id, name: element.name }));
    const current = { id: this.node.id, name: this.node.name };

    // Bibliotheques. « Sites » dans le chemin suffit a les reconnaitre : c'est un nom systeme,
    // stable et non traduit.
    const sites = elements.findIndex((element) => element.name === TreeNodesComponent.SITES);
    if (sites > -1) {
      const docLib = elements.findIndex((element) => element.name === TreeNodesComponent.DOCUMENT_LIBRARY);

      // Sous la bibliotheque : elle figure dans le chemin.
      if (docLib > -1) {
        const root = { id: elements[docLib].id, name: (elements[sites + 1]?.name ?? elements[docLib].name).toUpperCase() };
        return [root, ...elements.slice(docLib + 1), current];
      }

      // A l'ouverture d'une bibliotheque, le noeud courant EST `documentLibrary` — il n'est donc
      // pas dans son propre chemin. Sans ce cas, la recherche echouait, on retombait sur la
      // solution de repli, et l'arborescence demarrait a la racine du depot : l'utilisateur voyait
      // Data Dictionary, User Homes et tout le reste.
      //
      // `this.node.name` porte deja le titre du site : la page a appele `normalizeSitePath`, qui
      // renomme le conteneur avec `cm:title` du site avant de nous transmettre le noeud.
      return [{ id: current.id, name: current.name.toUpperCase() }];
    }

    // Fichiers personnels. Le dossier de l'utilisateur est celui qui suit « User Homes » ; s'il n'y
    // a rien apres, c'est que le noeud courant EST ce dossier.
    //
    // Il reste affiche, mais sous le libelle « Fichiers Personnels » et non sous le nom de compte :
    // c'est la ligne qui permet de revenir a la racine. Son nom d'utilisateur n'apporte rien a
    // celui qui le lit — il sait deja chez qui il est.
    const userHomes = elements.findIndex((element) => element.name === TreeNodesComponent.USER_HOMES);
    const home = userHomes > -1 ? userHomes + 1 : elements.findIndex((element) => element.name === this.authService.getUsername());

    if (home > -1) {
      const fromHome = elements.slice(home);
      const root = { id: fromHome[0]?.id ?? current.id, name: TreeNodesComponent.PERSONAL_FILES_LABEL };

      return fromHome.length ? [root, ...fromHome.slice(1), current] : [root];
    }

    // Ailleurs : on demarre au noeud courant. Aucune situation ne doit exposer l'arborescence du
    // depot a un utilisateur ordinaire.
    return [current];
  }

  /**
   * Construit l'arborescence en un seul aller-retour par niveau, tous lances en parallele.
   *
   * La version precedente enchainait, par niveau, un `getNode` PUIS un `getNodeChildren`, en
   * cascade d'abonnements imbriques — donc 2N requetes sequentielles pour une profondeur N. Les
   * `getNode` disparaissent (la chaine est deja dans `nodePath`) et les N appels d'enfants partent
   * ensemble.
   */
  private buildTree(): void {
    const levels = this.treeChain();

    forkJoin(levels.map((level) => this.nodesService.getNodeChildren(level.id)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (responses) => {
          const nodes: TreeNode[] = levels.map((level, index) => ({
            name: level.name,
            isFile: false,
            nodeId: level.id,
            nodeParentId: index > 0 ? levels[index - 1].id : undefined,
            expanded: true,
            current: index === levels.length - 1,
            children: []
          }));

          responses.forEach((response, index) => {
            const node = nodes[index];
            const next = nodes[index + 1];

            node.children = response.list.entries.map((child) => (next && next.nodeId === child.entry.id ? next : this.toTreeNode(child.entry)));

            // Le niveau suivant peut ne pas figurer dans la premiere page d'enfants ; on l'ajoute
            // alors en tete plutot que de casser la chaine.
            if (next && !node.children.includes(next)) {
              node.children.unshift(next);
            }

            if (response.list.pagination.hasMoreItems) {
              node.skipCount = response.list.pagination.skipCount + NODE_CHILDREN_PAGE_SIZE;
              node.children.push(this.loadNextRow(node.nodeId));
            }

            node.empty = node.children.length === 0;
            this.probeEmptiness(node.children);
            this.nodesAlreadyLoaded.add(node.nodeId);
          });

          this.treeData = [nodes[0]];
        },
        error: (error) => console.error("Erreur lors de la construction de l'arborescence", error)
      });
  }

  // Fonction récursive pour chercher l'objet par nodeId
  findNodeById(nodes: TreeNode[], targetNodeId: string): TreeNode | null {
    for (const node of nodes) {
      if (node.nodeId === targetNodeId) {
        return node; // Objet trouvé, retourner l'objet
      } else if (node.children && node.children.length > 0) {
        const foundInChildren = this.findNodeById(node.children, targetNodeId);
        if (foundInChildren) {
          return foundInChildren; // Objet trouvé dans les enfants, retourner l'objet
        }
      }
    }

    return null; // Aucun objet trouvé, retourner null
  }

  loadNext(nodeParentId: string) {
    // `rootNode` n'est renseigne que par les niveaux imbriques. Au premier niveau il est indefini,
    // et findNodeById iterait alors sur undefined.
    const parentNode = this.findNodeById(this.rootNode ?? this.treeData, nodeParentId);
    if (!parentNode) {
      return;
    }

    this.nodesService.getNodeChildren(parentNode.nodeId, parentNode.skipCount).subscribe((children) => {
      parentNode.children.splice(parentNode.children.length - 1, 1);
      children.list.entries.map((x) => {
        if (!this.nodesAlreadyLoaded.has(x.entry.id)) {
          parentNode.children.push(this.toTreeNode(x.entry));
        }
      });
      if (children.list.pagination.hasMoreItems) {
        parentNode.skipCount = children.list.pagination.skipCount + NODE_CHILDREN_PAGE_SIZE;
        parentNode.children.push(this.loadNextRow(nodeParentId));
      }
    });
  }
}
